import { useTranslation } from 'react-i18next';

import {
  DataPanelGroup,
  DataPanelInlineItem,
  DataPanelRow,
  NgButton,
  PanelErrorWithIcon,
  PanelInfoWithIcon,
  LocationSourceBadge,
} from '@/ui';
import { useIncidentLiveState } from '@ngsos/portal-core/portal';

import { isMeasuredLocation, LocationSource, WebAppState } from '@/signalr';
import type { Caller, Location } from '@/signalr';
import { locateCaller as locateCallerCommand, openManualGpsCoordinatesDialog } from '../../actions';
import { formatCoordinates, formatCoordinatesToDegree } from '@ngsos/portal-core/incidents';
import { IncidentDetailLocationSmsSentInfo } from '../../IncidentDetailLocationSmsSentInfo';
import { MapLinksPanelItem } from '../shared/MapLinksPanelItem';
import { ApproximateAddressPanelItem } from './ApproximateAddressPanelItem';
import { PositionInfoPanelRow } from './PositionInfoPanelRow';
import { RedactedLocationSummary } from './RedactedLocationSummary';

export type CallerLocationBlockProps = {
  incidentId: string;
  incidentIsReadOnly: boolean;
  callerPosition: Location | null;
  callerPositionHistory: readonly Location[];
  caller: Caller;
  chatIsActive: boolean;
};

function isLocationRelatedState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.None ||
    webAppState === WebAppState.LocationSmsSent ||
    webAppState === WebAppState.AppOpened ||
    webAppState === WebAppState.LocationPermissionRequested ||
    webAppState === WebAppState.LocationPermissionGranted ||
    webAppState === WebAppState.LocationUnavailableNotSupported ||
    webAppState === WebAppState.LocationPermissionDenied ||
    webAppState === WebAppState.LocationPositionUnavailable ||
    webAppState === WebAppState.LocationTimeout
  );
}

function isLocationErrorState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.LocationUnavailableNotSupported ||
    webAppState === WebAppState.LocationPermissionDenied ||
    webAppState === WebAppState.LocationPositionUnavailable ||
    webAppState === WebAppState.LocationTimeout
  );
}

export function CallerLocationBlock({
  incidentId,
  incidentIsReadOnly,
  callerPosition,
  callerPositionHistory,
  caller,
  chatIsActive,
}: CallerLocationBlockProps) {
  const { t } = useTranslation();
  const { webAppState } = useIncidentLiveState(incidentId);

  const locationStateText = (): string => {
    switch (webAppState) {
      case WebAppState.None:
      case WebAppState.LocationSmsSent:
        return t('WebAppState_Location_WaitingForLinkOpen');
      case WebAppState.AppOpened:
        return t('WebAppState_Location_AppOpened');
      case WebAppState.LocationPermissionRequested:
        return t('WebAppState_Location_PermissionRequested');
      case WebAppState.LocationPermissionGranted:
        return t('WebAppState_Location_PermissionGranted');
      case WebAppState.LocationUnavailableNotSupported:
        return t('WebAppState_Location_Unsupported');
      case WebAppState.LocationPermissionDenied:
        return t('WebAppState_Location_PermissionDenied');
      case WebAppState.LocationPositionUnavailable:
        return t('WebAppState_Location_PositionUnavailable');
      case WebAppState.LocationTimeout:
        return t('WebAppState_Location_Timeout');
      default:
        return t('WebAppState_Location_WaitingForLinkOpen');
    }
  };

  const locationStateDescription = (): string => {
    switch (webAppState) {
      case WebAppState.None:
      case WebAppState.LocationSmsSent:
        return t('WebAppState_Location_WaitingForLinkOpen_Description');
      case WebAppState.LocationPermissionRequested:
        return t('WebAppState_Location_PermissionRequested_Description');
      case WebAppState.LocationPermissionGranted:
        return t('WebAppState_Location_PermissionGranted_Description');
      case WebAppState.LocationUnavailableNotSupported:
        return t('WebAppState_Location_Unsupported_Description');
      case WebAppState.LocationPermissionDenied:
        return t('WebAppState_Location_PermissionDenied_Description');
      case WebAppState.LocationPositionUnavailable:
        return t('WebAppState_Location_PositionUnavailable_Description');
      case WebAppState.LocationTimeout:
        return t('WebAppState_Location_Timeout_Description');
      default:
        return '';
    }
  };

  const locateCallerConfirmationMessage = chatIsActive ? t('Incident_InfoPanel_ConfirmLocateCaller') : undefined;

  const locateCaller = async () => {
    await locateCallerCommand(incidentId);
  };

  const openManualGpsEditDialog = () => {
    openManualGpsCoordinatesDialog(incidentId);
  };

  if (callerPosition === null) {
    return (
      <div>

        <div className="bg-light p-2">
          {isLocationRelatedState(webAppState) && webAppState !== WebAppState.None ? (
            isLocationErrorState(webAppState) ? (
              <div className="mb-2">
                <PanelErrorWithIcon title={locationStateText()} description={locationStateDescription()} />
              </div>
            ) : (
              <>
                <PanelInfoWithIcon
                  icon="fal fa-location-crosshairs"
                  text={locationStateText()}
                  testID="incident-caller-location-waiting-text"
                />
                {locationStateDescription() !== '' && (
                  <div className="text-center small text-black-50 mt-1">{locationStateDescription()}</div>
                )}
              </>
            )
          ) : (
            <PanelInfoWithIcon
              icon="fal fa-location-crosshairs-slash"
              text={t('Incident_InfoPanel_UnknownCallerLocation')}
            />
          )}

          {!incidentIsReadOnly && (
            <>
              <div className="text-center small text-black-50 mt-2 mb-1">
                {t('Incident_InfoPanel_UnknownCallerLocation_Instructions')}
              </div>

              <div className="d-flex flex-wrap justify-content-center gap-1 mt-2">
                <NgButton
                  text={t('Incident_OpenEditGpsLocationDialog_NoPosition')}
                  icon="fal fa-pen-to-square"
                  size="small"
                  onClick={openManualGpsEditDialog}
                />

                <NgButton
                  text={t('NewIncidentDialog_Button_Localize')}
                  icon="fal fa-location"
                  size="small"
                  disabled={caller.phoneNumber === null}
                  enableMultipleClickPrevention
                  showProgress
                  minimumProgressTimeOutMs={3000}
                  confirmationMessage={locateCallerConfirmationMessage}
                  onClick={locateCaller}
                />
              </div>

              {caller.phoneNumber !== null && <IncidentDetailLocationSmsSentInfo />}
            </>
          )}
        </div>
      </div>
    );
  }

  if (callerPosition.type === 'Redacted') {
    return (
      <div>
          <RedactedLocationSummary location={callerPosition} />
      </div>
    );
  }

  const allowApproximateAddress = callerPosition.source !== LocationSource.AppleEed;

  return (
    <div>

      <div className="d-flex justify-content-between align-items-center mb-1">
        <LocationSourceBadge source={callerPosition.source} />
        <MapLinksPanelItem coordinates={callerPosition} />
      </div>

      <DataPanelGroup>
        <DataPanelRow>
          <DataPanelInlineItem
            title={t('Incident_InfoPanel_Coordinates')}
            value={formatCoordinatesToDegree(callerPosition)}
            allowCopyToClipboard
            valueToCopy={formatCoordinates(callerPosition)}
            copyTestID="incident-caller-location-coordinates-copy-button"
            testID="incident-caller-location-coordinates-text"
          ></DataPanelInlineItem>
        </DataPanelRow>

        {allowApproximateAddress && <ApproximateAddressPanelItem coordinates={callerPosition} />}

        {callerPosition.source !== LocationSource.Entered && isMeasuredLocation(callerPosition) && (
          <PositionInfoPanelRow lastIncidentPosition={callerPosition} />
        )}

        {!incidentIsReadOnly && callerPosition.source === LocationSource.Entered && (
          <DataPanelRow>
            <div className="d-flex flex-row gap-1 mt-2 w-100">
              <NgButton
                text={t('Incident_OpenEditGpsLocationDialog_ChangePosition')}
                icon="fal fa-pen-to-square"
                size="small"
                cssClass="w-100"
                onClick={openManualGpsEditDialog}
              />

              {callerPositionHistory.every(
                (position) => position.source === LocationSource.Entered || position.source === LocationSource.Aml,
              ) && (
                <NgButton
                  text={t('NewIncidentDialog_Button_Localize')}
                  icon="fal fa-location"
                  size="small"
                  disabled={caller.phoneNumber === null}
                  enableMultipleClickPrevention
                  showProgress
                  minimumProgressTimeOutMs={3000}
                  cssClass="w-100"
                  confirmationMessage={locateCallerConfirmationMessage}
                  onClick={locateCaller}
                />
              )}
            </div>
          </DataPanelRow>
        )}
      </DataPanelGroup>
    </div>
  );
}
